import React, { useState, useCallback } from 'react';
import MetricExplorer from './MetricExplorer';
import { formatCents } from './_money';

/*
 * RepReport — one rep's own report (Card P160 item 1, Daniel's top ask).
 *
 * ⭐ A CONFIGURATION, NOT A COMPONENT. It hands MetricExplorer the rep channel
 * names and the column allowlist, pre-scoped to one rep, and lets #722's
 * summary-first flow do the rest. The four figures each open the individual
 * records through the same path everything else uses.
 *
 * ⚠ THE REP IS A GROUP SCOPE, not a new parameter. `{dimension: 'rep', key:
 * repId}` is the ordinary allowlisted-column-plus-bound-value narrowing the
 * resolver already enforces — there is no rep-id threaded through the query
 * layer and so no new way to narrow a figure to an arbitrary person.
 *
 * ⚠ repId NULL is "Unassigned", and it is a REAL scope rather than a missing
 * one: the resolver's group scope turns a null key into `IS NULL`, which is
 * exactly the unassigned work Daniel asked to see as its own row.
 */

export const REP_CHANNELS = Object.freeze({
  summary: 'rep:drilldown:summary',
  resolve: 'rep:drilldown:resolve',
  export: 'rep:drilldown:export',
});

const OPP_COLUMNS = Object.freeze([
  { key: 'name', label: 'Name', sortAs: 'lastName' },
  { key: 'stage', label: 'Stage', sortAs: 'text' },
  { key: 'amount_cents', label: 'Value', sortAs: 'number', money: true, cents: true, align: 'right' },
  { key: 'created_at', label: 'Created', sortAs: 'date' },
  { key: 'owner', label: 'Owner', sortAs: 'text' },
]);

const TASK_COLUMNS = Object.freeze([
  { key: 'name', label: 'Contact', sortAs: 'lastName' },
  { key: 'title', label: 'Task', sortAs: 'text' },
  { key: 'due_date', label: 'Due', sortAs: 'date' },
  { key: 'days_overdue', label: 'Days over', sortAs: 'number', align: 'right' },
  { key: 'owner', label: 'Owner', sortAs: 'text' },
]);

const LEAD_COLUMNS = Object.freeze([
  { key: 'name', label: 'Name', sortAs: 'lastName' },
  { key: 'created_at', label: 'Created', sortAs: 'date' },
  { key: 'stage', label: 'Stage', sortAs: 'text' },
  { key: 'source', label: 'Source', sortAs: 'text' },
  { key: 'owner', label: 'Owner', sortAs: 'text' },
]);

/** Which column set a metric's rows use. Code-supplied, never derived. */
const COLUMNS_FOR = Object.freeze({
  'rep.overdue_tasks': TASK_COLUMNS,
  'rep.open_pipeline': OPP_COLUMNS,
  'rep.won': OPP_COLUMNS,
  'rep.new_leads': LEAD_COLUMNS,
});

export const REP_FIGURES = Object.freeze([
  { key: 'rep.overdue_tasks', label: 'Overdue follow-ups', unit: 'count' },
  { key: 'rep.open_pipeline', label: 'Open pipeline', unit: 'cents' },
  { key: 'rep.won', label: 'Deals won', unit: 'cents' },
  { key: 'rep.new_leads', label: 'Leads assigned', unit: 'count' },
]);

export default function RepReport({ rep, period, filters, onClose, onOpenContact, canExport }) {
  const [openMetric, setOpenMetric] = useState(null);
  const [figures, setFigures] = useState(null);
  const [failed, setFailed] = useState(false);

  const repId = rep ? rep.repId : undefined;
  const scope = { dimension: 'rep', key: repId === undefined ? null : repId };

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.invoke !== 'function') throw new Error('unavailable');
      const out = {};
      for (const f of REP_FIGURES) {
        const res = await api.invoke(REP_CHANNELS.summary, { key: f.key, dimension: 'rep', filters });
        if (res && res.error) throw new Error(res.message || 'failed');
        /*
         * ⚠ The rep's OWN group, picked out of the summary — not a second
         * query. The summary already groups by rep, so reading this rep's row
         * out of it is free and cannot disagree with the panel the figure opens.
         */
        const mine = (res.groups || []).find((g) =>
          (g.key === null && repId == null) || String(g.key) === String(repId));
        out[f.key] = mine ? { n: mine.n, totalCents: mine.totalCents } : { n: 0, totalCents: 0 };
      }
      setFigures(out);
    } catch (e) {
      // ⚠ Told it FAILED — never left holding null data it would render as zero.
      setFigures(null);
      setFailed(true);
    }
  }, [repId, filters]);

  React.useEffect(() => { load(); }, [load]);

  if (!rep) return null;

  if (openMetric) {
    return (
      <MetricExplorer
        metricKey={openMetric}
        title={`${rep.label} — ${(REP_FIGURES.find((f) => f.key === openMetric) || {}).label || ''}`}
        onClose={() => setOpenMetric(null)}
        onOpenContact={onOpenContact}
        canExport={canExport}
        channels={REP_CHANNELS}
        columns={COLUMNS_FOR[openMetric]}
        initialGroup={scope}
        filters={filters}
      />
    );
  }

  return (
    <section data-testid="rep-report" style={S.wrap}>
      <header style={S.head}>
        <div>
          <h3 style={S.title} data-testid="rep-report-title">{rep.label}</h3>
          <div style={S.sub}>{period ? `${period} · ` : ''}click a figure to open the records behind it</div>
        </div>
        <button type="button" onClick={onClose} style={S.close} data-testid="rep-report-close">Close</button>
      </header>

      {failed ? (
        <div style={S.error} data-testid="rep-report-failed">
          This rep&rsquo;s figures could not be loaded. They are unknown right now, not zero.
        </div>
      ) : (
        <div style={S.grid}>
          {REP_FIGURES.map((f) => {
            const v = figures && figures[f.key];
            return (
              <button key={f.key} type="button" style={S.fig}
                data-testid={`rep-figure-${f.key.replace('rep.', '')}`}
                onClick={() => setOpenMetric(f.key)}>
                <div style={S.figLabel}>{f.label}</div>
                <div style={S.figValue}>
                  {/*
                    * ⚠ Through the ONE formatter, told which unit it is given.
                    * The 28 Oct bug was a cents value handed to a dollars
                    * formatter; a figure that renders either way is a figure
                    * that can be wrong silently.
                    */}
                  {!figures ? '—'
                    : f.unit === 'cents' ? formatCents(v.totalCents)
                    : Number(v.n).toLocaleString('en-US')}
                </div>
                <div style={S.figSub}>
                  {!figures ? 'loading…' : f.unit === 'cents' ? `${v.n} record${v.n === 1 ? '' : 's'}` : 'open the records →'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

const S = {
  wrap: { border: '1px solid #DEE2E6', borderRadius: 12, padding: 16, marginTop: 16, background: '#fff' },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' },
  sub: { fontSize: 11.5, color: '#6C757D', marginTop: 2 },
  close: { background: 'none', border: '1px solid #CED4DA', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 },
  fig: { textAlign: 'left', border: '1px solid #F1F3F5', borderRadius: 10, padding: '11px 13px', background: 'none', cursor: 'pointer', font: 'inherit' },
  figLabel: { fontSize: 11, fontWeight: 700, color: '#495057' },
  figValue: { fontSize: 21, fontWeight: 700, color: '#212529', margin: '3px 0 2px' },
  figSub: { fontSize: 11, color: '#868E96' },
  error: { padding: 12, borderRadius: 10, background: '#FDECEA', color: '#7B241C', fontSize: 13 },
};
